import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { Figure, PageHeader, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { fetchCrsfnVotes } from '@/services/bacen';

/**
 * How the CRSFN votes on the linked acordaos: the unanimity mix, the
 * co-participation table (same side over CONTESTED shared acordaos, with the
 * all-acordaos share printed beside it and never alone), and per member how
 * often the bench went the other way.
 *
 * Every dispositive here is a roll call, so unlike CADE the agreement rate
 * is answered -- under three gates: a parsed roll call with every position
 * resolved, at least ten contested shared acordaos per pair, and the
 * nine-in-ten base rate of unanimity said on the panel. A WITH_MAJORITY
 * position is a subtraction (present and not named vencido), and the panel
 * says so.
 */
export function CrsfnVotesPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'crsfn', 'votes'],
    queryFn: fetchCrsfnVotes,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader page="bacen:crsfnVotes" exportPath="/api/v1/bacen/export/crsfn/votes" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-unanimity>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnVotes.unanimity.title')}
        </h2>
        <Answer answer={data.unanimity} help={t('bacen:crsfnVotes.unanimity.help')}>
          {(u) => (
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Figure
                label={t('bacen:crsfnVotes.unanimity.unanimous')}
                value={`${number.format(u.unanimous)} (${percent.format(u.unanimity_share)})`}
                testId="unanimous"
              />
              <Figure
                label={t('bacen:crsfnVotes.unanimity.contested')}
                value={number.format(u.contested)}
              />
              <Figure
                label={t('bacen:crsfnVotes.unanimity.unstated')}
                value={number.format(u.unstated)}
              />
              <Figure
                label={t('bacen:crsfnVotes.unanimity.relatorOverruled')}
                value={number.format(u.relator_overruled)}
                second={t('bacen:crsfnVotes.unanimity.ofRollCall', {
                  n: number.format(u.acordaos_with_roll_call),
                })}
              />
            </dl>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:crsfnVotes.guide.unanimity" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-agreement>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnVotes.agreement.title')}
        </h2>
        <Answer answer={data.agreement} help={t('bacen:crsfnVotes.agreement.help')}>
          {(a) => (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground" data-agreement-funnel>
                {t('bacen:crsfnVotes.agreement.funnel', {
                  pairs: number.format(a.total_pairs),
                  listed: number.format(a.pairs.length),
                  people: number.format(a.people),
                  threshold: number.format(a.threshold),
                  rollCall: number.format(a.acordaos_with_roll_call),
                  contested: number.format(a.acordaos_contested),
                  unresolved: number.format(a.acordaos_unresolved),
                  unanimity: percent.format(a.unanimity_share),
                })}
              </p>
              <div className="max-h-[60vh] overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:crsfnVotes.agreement.pair')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.agreement.shared')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.agreement.contestedShared')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.agreement.sameSideContested')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.agreement.sameSideAll')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.pairs.map((p) => (
                      <tr
                        key={`${p.slug_a}|${p.slug_b}`}
                        className="border-t"
                        data-pair={`${p.slug_a}|${p.slug_b}`}
                      >
                        <td className="px-3 py-2 text-foreground">
                          <Link
                            to={`/bacen/crsfn/r/${p.slug_a}`}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {p.name_a}
                          </Link>
                          <span className="text-muted-foreground"> · </span>
                          <Link
                            to={`/bacen/crsfn/r/${p.slug_b}`}
                            className="text-primary underline-offset-2 hover:underline"
                          >
                            {p.name_b}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(p.shared)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(p.contested_shared)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(p.same_side_contested)}
                          <span className="ml-1 text-xs text-muted-foreground">
                            (
                            {p.same_side_share_contested === null
                              ? '—'
                              : percent.format(p.same_side_share_contested)}
                            )
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {percent.format(p.same_side_share_all)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {a.below_floor > 0 ? (
                <p className="text-xs text-muted-foreground" data-below-floor>
                  {t('bacen:crsfnVotes.agreement.belowFloor', {
                    n: number.format(a.below_floor),
                    threshold: number.format(a.threshold),
                  })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:crsfnVotes.guide.agreement" />
        </div>
      </section>

      <section className="rounded-lg border bg-card p-4" data-dissent>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:crsfnVotes.dissent.title')}
        </h2>
        <Answer answer={data.dissent} help={t('bacen:crsfnVotes.dissent.help')}>
          {(d) => (
            <div className="space-y-3">
              <ul className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4 lg:grid-cols-7">
                {d.positions.map((p) => (
                  <li
                    key={p.code}
                    className="rounded-md border bg-muted/30 p-2"
                    data-position={p.code}
                  >
                    <div className="text-xs text-muted-foreground">{label('position', p.code)}</div>
                    <div className="font-semibold tabular-nums text-foreground">
                      {number.format(p.count)}
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        ({percent.format(p.share)})
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="max-h-[60vh] overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('bacen:crsfnVotes.dissent.member')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.dissent.participations')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.dissent.relator')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.dissent.redator')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.dissent.withMajority')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.dissent.vencido')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('bacen:crsfnVotes.dissent.unresolved')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.members.map((m) => (
                      <tr key={m.slug} className="border-t" data-member={m.slug}>
                        <td className="px-3 py-2 text-foreground">
                          {m.relator > 0 ? (
                            <Link
                              to={`/bacen/crsfn/r/${m.slug}`}
                              className="text-primary underline-offset-2 hover:underline"
                            >
                              {m.name}
                            </Link>
                          ) : (
                            m.name
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(m.participations)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(m.relator)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(m.redator)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(m.with_majority)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-foreground">
                          {number.format(m.vencido)}
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({m.vencido_share === null ? '—' : percent.format(m.vencido_share)})
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(m.unresolved)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {d.below_floor > 0 ? (
                <p className="text-xs text-muted-foreground" data-below-floor>
                  {t('bacen:crsfnVotes.dissent.belowFloor', {
                    n: number.format(d.below_floor),
                  })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:crsfnVotes.guide.dissent" />
        </div>
      </section>
    </div>
  );
}
